import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import GetQuotePage from "./pages/GetQuotePage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ApproveQuotePage from "./pages/ApproveQuotePage";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerProfile from "./pages/CustomerProfile";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSettings from "./pages/AdminSettings";
import AdminOrders from "./pages/AdminOrders";
import AdminUsers from "./pages/AdminUsers";
import AdminCompanies from "./pages/AdminCompanies";
import AdminQuoteRequests from "./pages/AdminQuoteRequests";
import AdminSavedQuotes from "./pages/AdminSavedQuotes";
import AdminCMS from "./pages/AdminCMS";
import AdminPDFSettings from "./pages/AdminPDFSettings";
import AdminChat from "./pages/AdminChat";
import AdminCreateOrder from "./pages/AdminCreateOrder";
import AdminCreateQuote from "./pages/AdminCreateQuote";
import AuthCallback from "./pages/AuthCallback";

// Components
import ChatWidget from "./components/ChatWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("auth_token");
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem("auth_token");
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.success) {
      localStorage.setItem("auth_token", response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
      return response.data.user;
    }
    throw new Error("Login failed");
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    if (response.data.success) {
      localStorage.setItem("auth_token", response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
      return response.data.user;
    }
    throw new Error("Registration failed");
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      googleLogin,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router - handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check for session_id in URL fragment BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes - Main entry is Get Quote */}
      <Route path="/" element={<GetQuotePage />} />
      <Route path="/get-quote" element={<Navigate to="/" replace />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/track-order" element={<TrackOrderPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/approve-quote/:token" element={<ApproveQuotePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Customer Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <CustomerProfile />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/orders" element={
        <ProtectedRoute adminOnly>
          <AdminOrders />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/customers" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute adminOnly>
          <AdminCompanies />
        </ProtectedRoute>
      } />
      <Route path="/admin/quote-requests" element={
        <ProtectedRoute adminOnly>
          <AdminQuoteRequests />
        </ProtectedRoute>
      } />
      <Route path="/admin/saved-quotes" element={
        <ProtectedRoute adminOnly>
          <AdminSavedQuotes />
        </ProtectedRoute>
      } />
      <Route path="/admin/cms" element={
        <ProtectedRoute adminOnly>
          <AdminCMS />
        </ProtectedRoute>
      } />
      <Route path="/admin/pdf-settings" element={
        <ProtectedRoute adminOnly>
          <AdminPDFSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/chat" element={
        <ProtectedRoute adminOnly>
          <AdminChat />
        </ProtectedRoute>
      } />
      <Route path="/admin/create-order" element={
        <ProtectedRoute adminOnly>
          <AdminCreateOrder />
        </ProtectedRoute>
      } />
      <Route path="/admin/create-quote" element={
        <ProtectedRoute adminOnly>
          <AdminCreateQuote />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Dashboard Router - routes to appropriate dashboard based on role
const DashboardRouter = () => {
  const { isAdmin } = useAuth();
  return isAdmin ? <AdminDashboard /> : <CustomerDashboard />;
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-[#050505]">
          <AppRouter />
          <ChatWidgetWrapper />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#111111',
                color: '#fff',
                border: '1px solid #27272A'
              }
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Chat Widget Wrapper - Only shows for non-admin users
const ChatWidgetWrapper = () => {
  const { isAdmin } = useAuth();
  // Don't show chat widget for admins (they use the admin chat page)
  if (isAdmin) return null;
  return <ChatWidget />;
};

export default App;
export { API, BACKEND_URL };
